import { useState } from 'react'
import type { Competitor } from '@ispot/shared'
import { CompetitorLogo } from './CompetitorLogo'

interface CompetitorTileProps {
  competitor: Competitor
  onOpen: (id: string) => void
}

const TYPE_COLOR: Record<Competitor['type'], string> = {
  direct: 'var(--color-accent)',
  adjacent: 'var(--color-market-dark)',
  potential: 'var(--color-text-secondary)',
}

const TYPE_LABEL: Record<Competitor['type'], string> = {
  direct: 'Direct',
  adjacent: 'Adjacent',
  potential: 'Potential',
}

export function CompetitorTile({ competitor, onOpen }: CompetitorTileProps) {
  const [hover, setHover] = useState(false)
  const isEnriching = competitor.enrichmentStatus === 'enriching'
  const isPending = competitor.enrichmentStatus === 'pending'
  const typeColor = TYPE_COLOR[competitor.type]
  const typeLabel = isEnriching ? 'Enriching…' : TYPE_LABEL[competitor.type]

  return (
    <button
      type="button"
      onClick={() => onOpen(competitor.id)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        appearance: 'none',
        font: 'inherit',
        color: 'inherit',
        background: hover ? 'var(--color-bg-secondary)' : 'transparent',
        border: 0,
        textAlign: 'left',
        cursor: 'pointer',
        padding: '8px 12px 8px 8px',
        borderRadius: '8px',
        display: 'grid',
        gridTemplateColumns: '36px 1fr',
        columnGap: '10px',
        alignItems: 'center',
        minWidth: '156px',
        maxWidth: '200px',
        transform: hover ? 'translateY(-1px)' : 'none',
        transition: 'background var(--dur-normal, 180ms) var(--ease-standard, ease), transform var(--dur-normal, 180ms) var(--ease-standard, ease)',
        opacity: isPending ? 0.75 : 1,
      }}
      aria-label={`${competitor.name} — open brief`}
      title={competitor.whatTheyDo ?? competitor.positioning ?? competitor.name}
    >
      <CompetitorLogo name={competitor.name} logoUrl={competitor.logoUrl} size={36} />
      <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 500,
            fontSize: '13.5px',
            color: 'var(--color-text)',
            letterSpacing: '-0.01em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.2,
          }}
        >
          {competitor.name}
        </span>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '10px',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: typeColor,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: '5px',
              height: '5px',
              borderRadius: '999px',
              background: typeColor,
              opacity: 0.8,
              display: 'inline-block',
            }}
          />
          {typeLabel}
        </span>
      </span>
    </button>
  )
}
